// src/components/sections/Experience.js
import React from 'react';
import { Server, Laptop, Database, Code, FileDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import { experienceData } from '../../data/experience';
import { motion } from 'framer-motion';

const Experience = () => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  const t = translations[language];
  
  const getIcon = (iconName) => {
    switch (iconName) {
      case 'Server': return <Server className="h-6 w-6" />;
      case 'Laptop': return <Laptop className="h-6 w-6" />;
      case 'Database': return <Database className="h-6 w-6" />;
      case 'Code': return <Code className="h-6 w-6" />;
      default: return <Server className="h-6 w-6" />;
    }
  };
  
  return (
    <section id="parcours" className={`py-16 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
      <div className="container mx-auto px-6">
        <motion.h2 
          className="text-3xl font-bold mb-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t["experience.title"]}
        </motion.h2>
        
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-0 md:left-1/2 ml-6 md:ml-0 transform md:-translate-x-1/2 h-full w-1 bg-blue-500"></div>
          
          {experienceData.map((experience, index) => (
            <motion.div 
              key={experience.id} 
              className="mb-12 relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <div className="flex flex-col md:flex-row items-start">
                {/* Pour les écrans moyens et grands - droite (pair) */}
                {index % 2 === 0 && (
                  <div className="md:w-1/2 md:pr-12 hidden md:block">
                    <div className="md:float-right text-right">
                      <div className={`inline-block p-5 rounded-lg shadow-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                        <h3 className="text-xl font-semibold mb-2">{experience.title[language]}</h3>
                        <p className={`mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          {typeof experience.company === 'object' ? experience.company[language] : experience.company}
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{experience.period[language]}</p>
                        <p className="mt-3">{experience.description[language]}</p>
                        
                        {/* Compétences */}
                        {experience.skills && (
                          <div className="mt-4">
                            <p className="text-sm font-semibold mb-2">{t["experience.skills"] || "Compétences"}</p>
                            <div className="flex flex-wrap justify-end gap-2">
                              {experience.skills.map((skill, idx) => (
                                <span 
                                  key={idx} 
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Pour les écrans moyens et grands - gauche (impair) */}
                {index % 2 === 1 && (
                  <div className="md:w-1/2 md:pr-12 hidden md:block"></div>
                )}
                
                {/* Icône du point de chronologie */}
                <div className="flex items-center justify-center ml-0 md:ml-6 md:w-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 shadow-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                    <div className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      {getIcon(experience.icon)}
                    </div>
                  </div>
                </div>
                
                {/* Pour les écrans moyens et grands - gauche (impair) */}
                {index % 2 === 1 && (
                  <div className="md:w-1/2 md:pl-12 hidden md:block">
                    <div className={`p-5 rounded-lg shadow-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <h3 className="text-xl font-semibold mb-2">{experience.title[language]}</h3>
                      <p className={`mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {typeof experience.company === 'object' ? experience.company[language] : experience.company}
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{experience.period[language]}</p>
                      <p className="mt-3">{experience.description[language]}</p>
                      
                      {/* Compétences */}
                      {experience.skills && (
                        <div className="mt-4">
                          <p className="text-sm font-semibold mb-2">{t["experience.skills"] || "Compétences"}</p>
                          <div className="flex flex-wrap gap-2">
                            {experience.skills.map((skill, idx) => (
                              <span 
                                key={idx} 
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                }`}
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Pour les écrans moyens et grands - droite (pair) */}
                {index % 2 === 0 && (
                  <div className="md:w-1/2 md:pl-12 hidden md:block"></div>
                )}
                
                {/* Pour mobile - toujours à droite de l'icône */}
                <div className="md:hidden pl-6">
                  <div className={`p-5 rounded-lg shadow-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                    <h3 className="text-xl font-semibold mb-2">{experience.title[language]}</h3>
                    <p className={`mb-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      {typeof experience.company === 'object' ? experience.company[language] : experience.company}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{experience.period[language]}</p>
                    <p className="mt-3">{experience.description[language]}</p>
                    
                    {/* Compétences pour mobile */}
                    {experience.skills && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold mb-2">{t["experience.skills"] || "Compétences"}</p>
                        <div className="flex flex-wrap gap-2">
                          {experience.skills.map((skill, idx) => (
                            <span 
                              key={idx} 
                              className={`px-2 py-1 text-xs rounded-full ${
                                darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              window.open('/assets/cv.pdf', '_blank');
            }}
            className={`inline-flex items-center px-6 py-3 rounded-lg ${
              darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
            } text-white transition-colors duration-200`}
          >
            <FileDown className="mr-2 h-5 w-5" />
            {t["experience.downloadCV"]}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;